import { useCallback, useEffect, useRef, useState } from 'react';
import { singleFlight, singleFlightCached } from '../utils/singleFlight';
import { safeStorage } from '../utils/safeStorage';

const RECENT_CREATED_TRIAL_KEY = 'gweffit_recent_created_trial';
const RECENT_CREATED_TRIAL_TTL_MS = 5 * 60 * 1000;

function areStringSetsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.size !== b.size) return false;
  for (const value of a) {
    if (!b.has(value)) return false;
  }
  return true;
}

function dedupeTrialsById(rows) {
  const seen = new Set();
  const deduped = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const stableId = String(row?.id || '');
    if (!stableId || seen.has(stableId)) continue;
    seen.add(stableId);
    deduped.push(row);
  }
  return deduped;
}

function readRecentCreatedTrialMarker() {
  try {
    const raw = safeStorage.get(RECENT_CREATED_TRIAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const trialId = String(parsed?.trialId || '').trim();
    const createdAt = Number(parsed?.createdAt || 0);
    if (!trialId || !Number.isFinite(createdAt)) return null;
    if ((Date.now() - createdAt) > RECENT_CREATED_TRIAL_TTL_MS) {
      safeStorage.remove(RECENT_CREATED_TRIAL_KEY);
      return null;
    }
    return { trialId, createdAt };
  } catch {
    return null;
  }
}

function clearRecentCreatedTrialMarker() {
  try {
    safeStorage.remove(RECENT_CREATED_TRIAL_KEY);
  } catch {}
}

function buildHomeCrewSignalMap({ trials, crews, crewMembers, voteRows, currentUserId }) {
  if (!Array.isArray(trials) || !trials.length || !Array.isArray(crews) || !crews.length) {
    return new Map();
  }

  const latestVotes = new Map();
  for (const vote of voteRows || []) {
    const trialId = vote?.trial_id;
    const userId = vote?.user_id;
    if (!trialId || !userId) continue;
    const key = `${trialId}:${userId}`;
    const nextTs = new Date(vote.created_at || 0).getTime();
    const prev = latestVotes.get(key);
    const prevTs = prev ? new Date(prev.created_at || 0).getTime() : -Infinity;
    if (!prev || nextTs >= prevTs) latestVotes.set(key, vote);
  }

  const membersByCrewId = new Map();
  for (const member of crewMembers || []) {
    const crewId = member?.crew_id;
    const userId = member?.user_id;
    if (!crewId || !userId) continue;
    const next = membersByCrewId.get(crewId) || [];
    next.push(userId);
    membersByCrewId.set(crewId, next);
  }

  const signalMap = new Map();

  for (const trial of trials) {
    const trialId = trial?.id;
    if (!trialId) continue;
    const status = String(trial?.status || '').toLowerCase();
    const isTerminal = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(status);

    const crewSignals = (crews || []).map((crew) => {
      const memberIds = membersByCrewId.get(crew.id) || [];
      let votesA = 0;
      let votesB = 0;
      let userChoice = null;

      memberIds.forEach((memberId) => {
        const vote = latestVotes.get(`${trialId}:${memberId}`);
        if (!vote) return;
        if (vote.vote_side === 'A') votesA += 1;
        if (vote.vote_side === 'B') votesB += 1;
        if (memberId === currentUserId) userChoice = vote.vote_side;
      });

      const totalCrewVotes = votesA + votesB;
      if (totalCrewVotes === 0) return null;

      const crewLeader = votesA === votesB ? null : (votesA > votesB ? 'A' : 'B');
      const crewLeaderLabel = crewLeader === 'A' ? trial.side_a : crewLeader === 'B' ? trial.side_b : null;
      const runnerUpLabel = crewLeader === 'A' ? trial.side_b : crewLeader === 'B' ? trial.side_a : null;
      const margin = Math.abs(votesA - votesB);
      const yourVoteCanSwing = !userChoice && totalCrewVotes > 0 && margin <= 1;
      const winnerSide = Number(trial.votes_a || 0) === Number(trial.votes_b || 0)
        ? null
        : Number(trial.votes_a || 0) > Number(trial.votes_b || 0) ? 'A' : 'B';
      const crewWon = winnerSide && crewLeader ? winnerSide === crewLeader : null;

      let line = null;
      if (!isTerminal) {
        if (yourVoteCanSwing) {
          line = `${crew.name || 'Your crew'} could swing to ${crewLeaderLabel || 'either side'}.`;
        } else if (crewLeaderLabel) {
          line = `${crew.name || 'Your crew'} is backing ${crewLeaderLabel}.`;
        } else {
          line = `${crew.name || 'Your crew'} is split.`;
        }
      } else if (crewWon === true) {
        line = `${crew.name || 'Your crew'} backed ${crewLeaderLabel} and won.`;
      } else if (crewWon === false && crewLeaderLabel && runnerUpLabel) {
        line = `${crew.name || 'Your crew'} backed ${crewLeaderLabel} and lost to ${runnerUpLabel}.`;
      } else {
        line = `${crew.name || 'Your crew'} finished split.`;
      }

      return {
        crewId: crew.id,
        crewName: crew.name || 'Your crew',
        crewEmoji: crew.emoji || '👥',
        totalCrewVotes,
        yourVoteCanSwing,
        line,
      };
    }).filter(Boolean);

    if (!crewSignals.length) continue;
    crewSignals.sort((a, b) => {
      if (Boolean(a.yourVoteCanSwing) !== Boolean(b.yourVoteCanSwing)) return a.yourVoteCanSwing ? -1 : 1;
      return (b.totalCrewVotes || 0) - (a.totalCrewVotes || 0);
    });
    signalMap.set(String(trialId), crewSignals[0]);
  }

  return signalMap;
}

export default function useHomeTrialsFeed({
  supabase,
  withTimeout,
  homeFeedScope,
  homeSearchTerm,
  user,
  canSeeQuarantinedTrials,
  locallyHiddenTrialIds, // accepted to keep callsite parity
  readLocallyHiddenTrialIds,
  setLocallyHiddenTrialIds,
  BETA_JURY_SIZE,
  finalizeCompletedRef,
  setFinalizeErrorTrials,
}) {
  const [trials, setTrials] = useState([]);
  const [trialsLoading, setTrialsLoading] = useState(true);
  const [trialsError, setTrialsError] = useState(null);
  const [followingCount, setFollowingCount] = useState(0);
  const trialsFetchTokenRef = useRef(0);
  const trialsRef = useRef([]);
  const TERMINAL_STATUSES = new Set(['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed']);

  useEffect(() => {
    trialsRef.current = trials;
  }, [trials]);

  const refetchTrials = useCallback(async () => {
    const fetchToken = ++trialsFetchTokenRef.current;
    try {
      // Avoid visible flashing when background/reflex refetches happen after feed is already populated.
      if (!Array.isArray(trialsRef.current) || trialsRef.current.length === 0) {
        setTrialsLoading(true);
      }
      setTrialsError(null);
      let data = null;
      let error = null;
      const feedKey = homeFeedScope === 'crews'
        ? `home_trials_feed:crews:${user?.id || 'guest'}`
        : homeFeedScope === 'following'
          ? `home_trials_feed:following:${user?.id || 'guest'}`
          : 'home_trials_feed:all';
      const shouldUseBootCache = !Array.isArray(trialsRef.current) || trialsRef.current.length === 0;

      if (homeFeedScope === 'crews' && user?.id) {
        const rpcResult = shouldUseBootCache
          ? await singleFlightCached(feedKey, 10_000, () => withTimeout(
              supabase.rpc('get_my_crews_feed_trials', { p_limit: 200, p_offset: 0 }),
              10_000,
              'fetch_my_crews_trials_timeout'
            ))
          : await singleFlight(feedKey, () => withTimeout(
              supabase.rpc('get_my_crews_feed_trials', { p_limit: 200, p_offset: 0 }),
              10_000,
              'fetch_my_crews_trials_timeout'
            ));
        data = rpcResult?.data ?? null;
        error = rpcResult?.error ?? null;
      } else if (homeFeedScope === 'following' && user?.id) {
        const followsResult = shouldUseBootCache
          ? await singleFlightCached(feedKey, 10_000, () => withTimeout(
              supabase
                .from('follows')
                .select('following_id')
                .eq('follower_id', user.id),
              10_000,
              'fetch_following_ids_timeout'
            ))
          : await singleFlight(feedKey, () => withTimeout(
              supabase
                .from('follows')
                .select('following_id')
                .eq('follower_id', user.id),
              10_000,
              'fetch_following_ids_timeout'
            ));

        const followsData = followsResult?.data ?? null;
        const followsError = followsResult?.error ?? null;
        if (followsError) throw followsError;

        const followedIds = [...new Set((followsData || []).map((row) => row?.following_id).filter(Boolean))];
        setFollowingCount(followedIds.length);
        if (followedIds.length === 0) {
          data = [];
          error = null;
        } else {
          const followedIdCsv = followedIds.join(',');
          const queryResult = await withTimeout(
            supabase
              .from('trials')
              .select('*')
              .eq('is_blocked', false)
              .or(`created_by.in.(${followedIdCsv}),creator_id.in.(${followedIdCsv})`)
              .order('created_at', { ascending: false }),
            10_000,
            'fetch_following_trials_timeout'
          );
          data = queryResult?.data ?? null;
          error = queryResult?.error ?? null;
        }
      } else if (homeFeedScope === 'following') {
        setFollowingCount(0);
        data = [];
        error = null;
      } else {
        setFollowingCount(0);
        const queryResult = shouldUseBootCache
          ? await singleFlightCached(feedKey, 10_000, () => withTimeout(
              supabase
                .from('trials')
                .select('*')
                .eq('is_blocked', false)
                .or('is_hidden.is.null,is_hidden.eq.false')
                .order('created_at', { ascending: false }),
              10_000,
              'fetch_trials_timeout'
            ))
          : await singleFlight(feedKey, () => withTimeout(
              supabase
                .from('trials')
                .select('*')
                .eq('is_blocked', false)
                .or('is_hidden.is.null,is_hidden.eq.false')
                .order('created_at', { ascending: false }),
              10_000,
              'fetch_trials_timeout'
            ));
        data = queryResult?.data ?? null;
        error = queryResult?.error ?? null;
      }

      if (error) throw error;
      if (fetchToken !== trialsFetchTokenRef.current) return;

      let dedupedData = dedupeTrialsById(data || []);
      const recentCreatedTrial = homeFeedScope === 'all' ? readRecentCreatedTrialMarker() : null;
      if (
        recentCreatedTrial?.trialId &&
        !dedupedData.some((row) => String(row?.id || '') === recentCreatedTrial.trialId)
      ) {
        const { data: recentCreatedRow, error: recentCreatedError } = await supabase
          .from('trials')
          .select('*')
          .eq('id', recentCreatedTrial.trialId)
          .maybeSingle();

        if (!recentCreatedError && recentCreatedRow?.id) {
          dedupedData = dedupeTrialsById([recentCreatedRow, ...dedupedData]);
        }
      }
      const creatorIds = [...new Set(dedupedData.map((t) => t.created_by).filter(Boolean))];

      let creatorMap = {};
      if (creatorIds.length) {
        const { data: creators, error: creatorsError } = await supabase
          .from('users')
          .select('id, username')
          .in('id', creatorIds);

        if (!creatorsError && creators) {
          creatorMap = creators.reduce((acc, u) => {
            acc[u.id] = u.username;
            return acc;
          }, {});
        }
      }

      const votedTrialIds = new Set();
      let homeCrewSignalMap = new Map();

      if (user?.id && dedupedData.length) {
        const trialIds = dedupedData.map((t) => t.id).filter(Boolean);

        const { data: votedRows, error: votedError } = await supabase
          .from('votes')
          .select('trial_id')
          .eq('user_id', user.id)
          .in('trial_id', trialIds);

        if (!votedError && votedRows) {
          votedRows.forEach((r) => votedTrialIds.add(r.trial_id));
        } else if (votedError) {
          console.warn('⚠️ Could not load voted trials:', votedError);
        }

        try {
          const { data: membershipRows, error: membershipErr } = await supabase
            .from('crew_members')
            .select('crew_id, crews(id,name,emoji)')
            .eq('user_id', user.id);
          if (membershipErr) throw membershipErr;

          const crews = (membershipRows || []).map((row) => row?.crews).filter((crew) => crew?.id);
          const crewIds = [...new Set(crews.map((crew) => crew.id))];
          if (crewIds.length > 0 && trialIds.length > 0) {
            const { data: crewMemberRows, error: crewMemberErr } = await supabase
              .from('crew_members')
              .select('crew_id,user_id')
              .in('crew_id', crewIds);
            if (crewMemberErr) throw crewMemberErr;

            const memberIds = [...new Set((crewMemberRows || []).map((row) => row?.user_id).filter(Boolean))];
            if (memberIds.length > 0) {
              const { data: crewVoteRows, error: crewVoteErr } = await supabase
                .from('votes')
                .select('trial_id,user_id,vote_side,created_at')
                .in('trial_id', trialIds)
                .in('user_id', memberIds);
              if (crewVoteErr) throw crewVoteErr;

              homeCrewSignalMap = buildHomeCrewSignalMap({
                trials: dedupedData,
                crews,
                crewMembers: crewMemberRows || [],
                voteRows: crewVoteRows || [],
                currentUserId: user.id,
              });
            }
          }
        } catch (crewSignalErr) {
          console.warn('⚠️ Could not load home crew signals:', crewSignalErr?.message || crewSignalErr);
        }
      }

      const hiddenSet = readLocallyHiddenTrialIds ? readLocallyHiddenTrialIds() : new Set(locallyHiddenTrialIds || []);
      const visibleData = dedupedData
        .filter((trial) => !TERMINAL_STATUSES.has(String(trial?.status || '').toLowerCase()))
        .filter((trial) => canSeeQuarantinedTrials || !trial?.is_quarantined)
        .filter((trial) => !hiddenSet.has(String(trial?.id || '')));

      if (recentCreatedTrial?.trialId && visibleData.some((trial) => String(trial?.id || '') === recentCreatedTrial.trialId)) {
        clearRecentCreatedTrialMarker();
      }

      const formattedTrials = visibleData.map((trial) => {
        let timeLeft = null;
        if (trial.expires_at) {
          const expiresAt = new Date(trial.expires_at).getTime();
          const now = Date.now();
          timeLeft = Math.max(0, Math.floor((expiresAt - now) / 1000));
        }

        const timeLimitSeconds = trial.time_limit_seconds ||
          (trial.expires_at && trial.created_at
            ? Math.floor((new Date(trial.expires_at) - new Date(trial.created_at)) / 1000)
            : 120);

        return {
          id: trial.id,
          type: trial.trial_type,
          trialMode: trial.trial_mode ?? 'debate',
          decisionCategory: trial.decision_category ?? null,
          title: trial.title,
          sideA: trial.side_a,
          sideB: trial.side_b,
          votesA: trial.votes_a,
          votesB: trial.votes_b,
          totalVotes: trial.total_votes,
          status: trial.status,
          image: trial.image_emoji,
          jurySize: trial.jury_size || BETA_JURY_SIZE,
          jurorsFilled: trial.jurors_filled || 0,
          timeLeft,
          timeLimitSeconds,
          expiresAt: trial.expires_at,
          verdict_data: trial.verdict_data,
          createdAt: new Date(trial.created_at).getTime(),
          closedAt: trial.closed_at ? new Date(trial.closed_at).getTime() : null,
          duration: trial.trial_type === 'quick' ? '2min' : '24h',
          hasVoted: votedTrialIds.has(trial.id),
          recentVotes: 0,
          lastVoteTime: Date.now(),
          featured: true,
          suddenDeath: String(trial.status || '').toLowerCase() === 'sudden_death_open',
          suddenDeathStartedAt: trial.sudden_death_started_at || null,
          suddenDeathEndsAt: trial.sudden_death_ends_at || trial.expires_at || null,
          suddenDeathRound: Number(trial.sudden_death_round || 0),
          suddenDeathResolutionMethod: trial.sudden_death_resolution_method || null,
          is_hidden: Boolean(trial.is_hidden),
          is_quarantined: Boolean(trial.is_quarantined),
          is_seeded: Boolean(trial.is_seeded),
          seed_source: trial.seed_source || null,
          created_by: trial.created_by,
          creator_id: trial.creator_id,
          creator_username: creatorMap[trial.created_by] || null,
          homeCrewSignal: homeCrewSignalMap.get(String(trial.id)) || null,
        };
      });

      formattedTrials.forEach((trial) => {
        const status = String(trial.status || '').toLowerCase();
        if (['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(status)) {
          finalizeCompletedRef.current.add(trial.id);
        } else {
          finalizeCompletedRef.current.delete(trial.id);
        }
      });
      setFinalizeErrorTrials((prev) => {
        if (!prev.size) return prev;
        const next = new Set(prev);
        formattedTrials.forEach((trial) => {
          const status = String(trial.status || '').toLowerCase();
          if (['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'].includes(status)) {
            next.delete(trial.id);
          }
        });
        return next;
      });
      if (fetchToken !== trialsFetchTokenRef.current) return;
      if (setLocallyHiddenTrialIds) {
        setLocallyHiddenTrialIds((prev) => {
          const prevSet = prev instanceof Set ? prev : new Set(prev || []);
          return areStringSetsEqual(prevSet, hiddenSet) ? prev : hiddenSet;
        });
      }
      setTrials(formattedTrials);
      trialsRef.current = formattedTrials;
    } catch (err) {
      console.error('❌ Error loading trials:', err);
      setTrialsError(err);
    } finally {
      if (fetchToken === trialsFetchTokenRef.current) {
        setTrialsLoading(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    BETA_JURY_SIZE,
    canSeeQuarantinedTrials,
    finalizeCompletedRef,
    homeFeedScope,
    readLocallyHiddenTrialIds,
    setFinalizeErrorTrials,
    setLocallyHiddenTrialIds,
    supabase,
    user?.id,
    withTimeout,
  ]);

  useEffect(() => {
    if (String(homeSearchTerm || '').trim()) return;
    refetchTrials();
  }, [homeFeedScope, homeSearchTerm, refetchTrials]);

  const invalidateTrialsFetch = useCallback(() => {
    trialsFetchTokenRef.current += 1;
  }, []);

  const stopTrialsLoading = useCallback(() => {
    setTrialsLoading(false);
  }, []);

  return {
    trials,
    setTrials,
    trialsLoading,
    trialsError,
    followingCount,
    refetchTrials,
    invalidateTrialsFetch,
    stopTrialsLoading,
  };
}
